import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export interface PasswordToggleButtonProps {
  /** Whether the password is currently revealed (input type === "text"). */
  visible: boolean;
  /** Toggle handler. */
  onToggle: () => void;
  disabled?: boolean;
  /** Distance (px) from the right edge of the relatively-positioned wrapper. */
  right?: number;
  /** id of the input this button controls, wired to aria-controls. */
  controls?: string;
}

/**
 * Accessible show/hide toggle for password inputs.
 *
 * Designed to be dropped into any `position: relative` wrapper that contains a
 * password `<input>` (the bespoke auth-page pattern). The host input should
 * reserve ~40px of right padding so the button does not overlap typed text, and
 * should switch its `type` between `"password"` and `"text"` based on `visible`.
 *
 * Hover/focus affordances are CSS-driven (`.mc-password-toggle` in index.css) to
 * avoid JS-managed hover state.
 */
export default function PasswordToggleButton({
  visible,
  onToggle,
  disabled = false,
  right = 8,
  controls,
}: PasswordToggleButtonProps) {
  const Icon = visible ? EyeSlashIcon : EyeIcon;
  return (
    <button
      type="button"
      className="mc-password-toggle"
      onClick={onToggle}
      disabled={disabled}
      aria-label={visible ? 'Hide password' : 'Show password'}
      aria-pressed={visible}
      aria-controls={controls}
      title={visible ? 'Hide password' : 'Show password'}
      style={{
        position: 'absolute',
        right: `${right}px`,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '28px',
        height: '28px',
        padding: 0,
        margin: 0,
        border: 'none',
        background: 'transparent',
        color: 'var(--color-text-muted)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderRadius: '6px',
      }}
    >
      <Icon style={{ width: '16px', height: '16px' }} aria-hidden="true" />
    </button>
  );
}
